import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Convert API error response to a user-friendly string message.
 * Handles various error formats: string, array of objects, object with detail.
 * Prevents React crashes from attempting to render non-string errors.
 */
const toErrorMessage = (errorData, fallback = 'An error occurred') => {
  if (!errorData) return fallback;
  
  // String - return as-is
  if (typeof errorData === 'string') return errorData;
  
  // Object with detail field (FastAPI standard)
  if (errorData.detail) {
    // detail can be string or array (Pydantic validation errors)
    if (typeof errorData.detail === 'string') return errorData.detail;
    
    // Array of validation errors: [{ loc: ['body', 'email'], msg: '...', type: '...' }]
    if (Array.isArray(errorData.detail)) {
      const messages = errorData.detail
        .map(err => err.msg || err.message || JSON.stringify(err))
        .filter(Boolean);
      return messages.length > 0 ? messages.join('. ') : fallback;
    }
    
    // Object detail - stringify safely
    return JSON.stringify(errorData.detail);
  }
  
  // Array at top level
  if (Array.isArray(errorData)) {
    const messages = errorData
      .map(err => err.msg || err.message || err.detail || JSON.stringify(err))
      .filter(Boolean);
    return messages.length > 0 ? messages.join('. ') : fallback;
  }
  
  // Generic object - try message field or stringify
  if (errorData.message) return errorData.message;
  if (errorData.msg) return errorData.msg;
  
  return fallback;
};

// Create axios instance with credentials support for httpOnly cookies
const authApi = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important: Send cookies with requests
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Keep token in memory only (not localStorage) for WebSocket and fallback
  const [token, setToken] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      // Create AbortController for 8-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      // Authenticate with httpOnly cookie
      const response = await authApi.get('/auth/me', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      setUser(response.data);
      // Store token in memory if returned (for WebSocket connections)
      if (response.data.token) {
        setToken(response.data.token);
      }
    } catch (error) {
      // Cookie auth failed, timeout, or network error - user not logged in
      // This is NOT a crash condition - just means user needs to log in
      console.debug('[Auth] fetchUser failed (user not logged in):', error.message);
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Start auth check
    fetchUser();
    
    // Safety timeout: Force loading = false after 8 seconds max
    // Prevents infinite loading screens on slow networks or API failures
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 8000);
    
    return () => clearTimeout(timeout);
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await authApi.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      // Store token in memory (httpOnly cookie handles auth for API calls)
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      if (error.response?.status === 403 && error.response?.headers?.['x-2fa-required']) {
        return { success: false, requires2FA: true };
      }
      if (error.response?.status === 423) {
        const minutes = error.response.headers?.['x-locked-minutes'] || 15;
        return { success: false, error: i18n.t('notices.accountLocked', { minutes }) };
      }
      // Handle rate limiting specifically
      if (error.response?.status === 429) {
        return { success: false, error: 'Too many login attempts. Please wait a few minutes before trying again.' };
      }
      // Use toErrorMessage to safely convert any error format to string
      return { success: false, error: toErrorMessage(error.response?.data, 'Login failed') };
    }
  };

  const loginWith2FA = async (email, password, code) => {
    try {
      const response = await authApi.post('/auth/login-2fa', { email, password, code });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      if (error.response?.status === 423) {
        const minutes = error.response.headers?.['x-locked-minutes'] || 15;
        return { success: false, error: i18n.t('notices.accountLocked', { minutes }) };
      }
      // Handle rate limiting specifically
      if (error.response?.status === 429) {
        return { success: false, error: 'Too many login attempts. Please wait a few minutes before trying again.' };
      }
      // Use toErrorMessage to safely convert any error format to string
      return { success: false, error: toErrorMessage(error.response?.data, 'Login failed') };
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await authApi.post('/auth/register', {
        email,
        password,
        full_name: fullName
      });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      // Use toErrorMessage to safely convert any error format to string
      return { success: false, error: toErrorMessage(error.response?.data, 'Registration failed') };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie
      await authApi.post('/auth/logout');
    } catch (error) {
      // Logout API failed - still proceed with local cleanup
      // This can happen if the session already expired
      console.debug('Logout API call failed:', error.message);
    }
    
    // Clear in-memory state
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const refreshToken = async () => {
    try {
      await authApi.post('/auth/refresh');
      return true;
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWith2FA,
    register,
    logout,
    refreshUser,
    refreshToken,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
